import { DocumentParameters, HgsArticle, HgsArticleOld } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, Input, effect, inject, signal } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { HgsMarkdownComponent } from '@app/article/renderer/hgs/hgs-markdown.component';
import { LoadingDotsComponent } from '@app/chat/components/loading-dots/loading-dots.component';
import { HgsFuseboxComponent } from '../renderer/hgs/hgs-fuseboxes.component';
import { HgsInspectionComponent } from '../renderer/hgs/hgs-inspection.component';
import { HgsLocationComponent } from '../renderer/hgs/hgs-location.component';
import { HgsServiceSchedulesComponent } from '../renderer/hgs/hgs-service-schedules.component';
import { HgsWiringDiagramComponent } from '../renderer/hgs/hgs-wiring-diagram.component';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-hgs-articles',
  template: `
    @if (isLoading()) {
      <app-loading-dots />
    } @else {
      @if (documentParameters?.type === 'hgs-location') {
        <app-hgs-location [data]="detailsData()" />
      } @else if (documentParameters?.type === 'hgs-wiring-diagram') {
        <app-hgs-wiring-diagram [data]="detailsData()" />
      } @else if (documentParameters?.type === 'hgs-fusebox') {
        <app-hgs-fusebox [data]="detailsData()" />
      } @else if (documentParameters?.type === 'hgs-service-schedule') {
        <app-hgs-service-schedules [data]="detailsData()" />
      } @else if (documentParameters?.type === 'hgs-inspection') {
        <app-hgs-inspection [data]="detailsData()" />
      } @else {
        <app-hgs-renderer [data]="detailsData()" />
      }
    }
  `,
  imports: [
    LoadingDotsComponent,
    HgsMarkdownComponent,
    HgsLocationComponent,
    HgsWiringDiagramComponent,
    HgsFuseboxComponent,
    CommonModule,
    HgsServiceSchedulesComponent,
    HgsInspectionComponent,
  ],
  standalone: true,
})
export class HgsArticleComponent {
  @Input({ required: true }) documentParameters?: DocumentParameters;

  api3Service = inject(Api3Service);

  detailsData = signal<HgsArticle | undefined>(undefined);
  isLoading = signal(true);

  init = effect(async () => {
    if (!this.documentParameters) {
      console.warn('[HgsArticleComponent] DocumentParameters is undefined or missing.');
      this.isLoading.set(false);
      return;
    }

    if (this.documentParameters.errorCode) {
      const documents = await this._loadErrorCodeData(this.documentParameters);
      this.detailsData.set(documents as HgsArticle | undefined);
    } else {
      const document = await this._loadHgsNewData(this.documentParameters);
      // TODO: as the API has not yet proper object types, we assume here that the returned document has a 'text' field
      const data = document as HgsArticle | undefined;
      this.detailsData.set(data);
    }

    this.isLoading.set(false);
  });

  private async _loadErrorCodeData(documentParameters: DocumentParameters): Promise<HgsArticleOld | undefined> {
    // type: 'hgsErrorCode'
    const { data: document, error } = await this.api3Service.api.hgs.getDocument({
      type: documentParameters.type,
      kType: documentParameters.kType,
      vin: documentParameters.vin,
      engineCode: documentParameters.engineCode,
      errorCode: documentParameters.errorCode,
    });

    if (error) {
      console.error('[HgsArticleComponent] Error loading articles:', error);
      const sentryError = new Error(`Failed to load article data: ${error.message || 'Unknown error'}`);
      Sentry.captureException(sentryError, {
        tags: {
          location: 'HgsArticleComponent',
          feature: 'ArticleView',
        },
        extra: {
          httpError: error,
        },
      });
      return undefined;
    }

    return document;
  }

  // Examples for HGS new data:
  // type=hgs-techdata&techDataGroupId=45511&language=de&engineCode=BCA&kType=8799&articleView=true&provider=hgs
  // type=hgs-wiring-diagram&hgsSystemId=W-17476-0-486--&language=de&engineCode=BCA&kType=8799&articleView=true&provider=hgs
  // type=hgs-location&hgsComponentId=E-82&language=de&engineCode=BCA&kType=8799&articleView=true&provider=hgs
  // not yet implemented:
  // type=hgs-recall-campaign&language=de&engineCode=BCA&kType=8799&articleView=true&provider=hgs
  private async _loadHgsNewData(documentParameters: DocumentParameters): Promise<object | undefined> {
    // type: 'hgs-wiring-diagram' | 'hgs-techdata'| 'hgs-location' | 'hgs-recall-campaign'

    const { data: document, error } = await this.api3Service.api.hgsNew.getDocument({
      ...documentParameters,
    });

    if (error) {
      console.error('[HgsArticleComponent] Error loading articles:', error);
      const sentryError = new Error(`Failed to load article data: ${error.message || 'Unknown error'}`);
      Sentry.captureException(sentryError, {
        tags: {
          location: 'HgsArticleComponent',
          feature: 'ArticleView',
        },
        extra: {
          httpError: error,
        },
      });
      return undefined;
    }

    return document;
  }
}
